import { useState } from 'react';
import { useAuthStore } from '../stores/authStore';
import {  Mail, User, CheckCircle, Loader2 } from 'lucide-react';
import { employeeService } from '../services/employeeService';
import { toast } from 'react-hot-toast';

export function ProfilePage() {
    const user = useAuthStore(state => state.user);
    const [name, setName] = useState(user?.name || '');
    const [isLoading, setIsLoading] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    // @ts-ignore - department property exists in runtime
    const [department] = useState(user?.department || '');

    // Get user initials
    const getInitials = (name: string) => {
        if (!name) return '';
        const parts = name.split(' ');
        if (parts.length === 1) return parts[0][0].toUpperCase();
        const firstInitial = parts[0][0].toUpperCase();
        const lastInitial = parts[parts.length - 1]?.[0]?.toUpperCase() || '';
        return `${firstInitial}${lastInitial}`;
    };

    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newName = e.target.value;
        setName(newName);
        setHasChanges(newName !== (user?.name || ''));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!name.trim()) {
            toast.error('Please enter a valid name');
            return;
        }

        if (!hasChanges) {
            toast('No changes to save', { icon: 'ℹ️' });
            return;
        }

        setIsLoading(true);
        
        try {
            await employeeService.updateProfile(name);
            
            // Show success notification with more details
            toast.success(
                <div>
                    <div className="font-medium">Profile Updated!</div>
                    <div className="text-sm text-gray-600">Your name has been changed to "{name}"</div>
                </div>,
                { duration: 4000 }
            );
            
            setHasChanges(false);
        } catch (error) {
            console.error('Error updating profile:', error);
            toast.error(
                <div>
                    <div className="font-medium">Failed to update profile</div>
                    <div className="text-sm text-gray-600">Please try again or contact support</div>
                </div>,
                { duration: 4000 }
            );
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="max-w-2xl mx-auto p-6">
            <div className="bg-white rounded-lg shadow-sm border border-slate-200">
                <div className="p-6">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-2xl font-semibold text-foreground">Profile Settings</h2>
                        {hasChanges && (
                            <div className="flex items-center space-x-2 text-amber-600 bg-amber-50 px-3 py-1 rounded-full text-sm">
                                <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse"></div>
                                <span className="font-medium">Unsaved changes</span>
                            </div>
                        )}
                    </div>
                    
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="flex items-center space-x-4">
                            <div className="w-20 h-20 rounded-full bg-primary-100 flex items-center justify-center">
                                <span className="text-2xl font-semibold text-primary-600">
                                    {getInitials(name)}
                                </span>
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <User className="h-5 w-5 text-slate-400" />
                                    </div>
                                    <input
                                        type="text"
                                        value={name}
                                        onChange={handleNameChange}
                                        className="block w-full pl-10 pr-3 py-2 text-lg font-medium text-foreground bg-transparent border-b border-slate-200 focus:border-primary-500 focus:outline-none"
                                        placeholder="Enter your name"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label htmlFor="email" className="block text-sm font-medium text-foreground mb-1">
                                    Email
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <Mail className="h-5 w-5 text-slate-400" />
                                    </div>
                                    <input
                                        type="email"
                                        id="email"
                                        value={user?.email || ''}
                                        disabled
                                        className="block w-full pl-10 pr-3 py-2 border border-slate-300 rounded-md bg-slate-50 text-slate-500"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-center lg:justify-end">
                            <button
                                type="submit"
                                disabled={isLoading || !hasChanges}
                                className={`px-6 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-all duration-200 flex items-center space-x-2 ${
                                    isLoading || !hasChanges
                                        ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
                                        : 'bg-primary-600 text-white hover:bg-primary-700 hover:shadow-md'
                                }`}
                            >
                                {isLoading ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        <span>Saving...</span>
                                    </>
                                ) : hasChanges ? (
                                    <>
                                        <CheckCircle className="w-4 h-4" />
                                        <span>Save Changes</span>
                                    </>
                                ) : (
                                    <span>No Changes</span>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
} 